/*
 * IcInventurHochladen.tsx
 *
 * Created by Dr. Maximillian Dornseif 2022-12-31 in huWaWi3 31.12.0
 * Copyright (c) 2022, 2023 HUDORA GmbH
 */

import { LinkRender } from '../lib/hd-link'
import { LadeChecker } from '@hudora/hd-react-components'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import { assert, assertIsString } from 'assertate-debug'
import gql from 'graphql-tag'
import { parse as parseCsv } from 'papaparse'
import React, { useState } from 'react'
import ReactJson from 'react-json-view'
import { useMutation, useQuery } from 'urql'

import { KindLightList } from '../eAP/KindLightList'
import { INVENTURBESTAETIGUNG_FRAGMENT } from '../fragments'
import { INVENTUR_NAME } from './IcInventurApp'
import { EINEBESTAETIGUNG_QUERY, GEZAEHLT_QUERY, UNGEZAEHLT_QUERY } from './IcInventurQueries'

export const IcInventurHochladen = () => {
  const [rawCsv, setRawCsv] = useState('')
  const [csvErrors, setCsvErrors] = useState<string[]>([])
  const [saveData, setSaveData] = useState<Record<string, boolean | null | string | Number | Date>[]>([])

  // Parsen der bisher eingegebenen CSV Daten
  const {
    data: csvData,
    errors: newCsvErrors,
    meta,
  } = parseCsv<Record<string, boolean | null | string | Number | Date>>(rawCsv, {
    dynamicTyping: true,
    header: true,
    comments: '#',
    skipEmptyLines: 'greedy',
    delimitersToGuess: [',', '\t', '|', ';'],
  })

  // Errors in der GUI loggen - das ist etwas verzwickt, um re-renders zu vermeiden
  if (newCsvErrors && newCsvErrors.length > 0) {
    const newCsvErrorList = newCsvErrors.map((x) => x.message)
    if (JSON.stringify(csvErrors) !== JSON.stringify(newCsvErrorList)) {
      setCsvErrors(newCsvErrorList)
    }
  } else if (csvErrors.length > 0) {
    setCsvErrors([])
  }

  const alleSpaltenDa =
    meta?.fields.includes('sku') && meta?.fields.includes('menge') && meta?.fields.includes('lager')

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} justifyContent="center">
        <div style={{ width: '25%' }}>
          <h2>1. Noch ungezählt</h2>
          <IcInventurHochlagenUnbestaetigt />
        </div>
        <div style={{ width: '25%' }}>
          <h2>2. Zählergebnisse eingeben</h2>

          <p>
            Bitte Kopieren Sie die Zählergebnisse aus Ihrer Tabellenkalkulation direkt in das unten stehende
            Feld. Benötigt sind die Spalten Lager (gezählte) Menge und Sku. Die erste Zeile sollte die
            Spaltennamen `lager;menge;sku` beinhalten. Sie können auch eine Spalte "info" zufügen.
            Trennzeichen kann <code>;</code>, <code>, </code>, <code>|</code> oder der Tabulator{' '}
            <code>\t</code> sein.
          </p>

          <textarea
            aria-label="CSV-Eingabe"
            placeholder={`lager;menge;sku;info
LG200;11;14100x01;
LG200;11;14200x01;Fehlbestand wohl durch Umbuchung
LG200;11;14100x03;
`}
            style={{ width: '100%', minWidth: '25em' }}
            rows={10}
            value={rawCsv}
            onChange={(event) => setRawCsv(event.target.value)}
          ></textarea>
          {csvData ? <p title="Zeilenzahl">{csvData?.length} Zeilen</p> : null}
          {csvData?.length > 0 && csvErrors.length > 0 ? (
            <div title="Eingabe-Fehler">
              <p>Probleme mit den Eingabe-Daten:</p>
              <ul>
                {csvErrors.map((x) => (
                  <li key={x}>{x}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {meta?.fields && meta?.fields.length > 0 ? (
            <div title="Spalten">
              <p>Erkannte Spalten:</p>
              <ul>
                {meta?.fields.map((x) => (
                  <li key={x}>{x}</li>
                ))}
              </ul>
              {!alleSpaltenDa ? (
                <span>Es sind nicht alle Spalten (lager,menge,sku) vorhanden!</span>
              ) : (
                <span>Die Daten können so, wie sie jetzt sind, verarbeitet werden.</span>
              )}
            </div>
          ) : null}
          <ReactJson name="Tabellen-Details" collapsed src={meta} />
        </div>
        <div style={{ width: '25%' }}>
          <h2>3. Zählergebnisse speichern</h2>
          {!csvData.length ? (
            <p>Geben Sie links die Zählliste ein.</p>
          ) : (
            <div>
              <Button
                variant="contained"
                // disabled={Object.values(positionen).length < 1 || !isEmpty(errors)}
                onClick={() => {
                  setSaveData(csvData)
                  setRawCsv('')
                }}
              >
                Zählergebnisse speichern
              </Button>
              <div>
                <p>Erkannte Zählergebnisse:</p>

                <table className="hd-table">
                  <thead>
                    <tr>
                      <th>Lager</th>
                      <th>Menge</th>
                      <th>SKU</th>
                    </tr>
                  </thead>
                  <tbody>
                    {csvData.map((row) => (
                      <tr key={`${row.lager}_${row.sku}`}>
                        <td>{`${row.lager}`}</td>
                        <td align="right">{`${row.menge}`}</td>
                        <td>{`${row.sku}`}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {saveData.length > 0 ? (
            <div>
              <p>Zu speichernde Zählergebnisse:</p>
              <table className="hd-table">
                <thead>
                  <tr>
                    <th>Lager</th>
                    <th>Menge</th>
                    <th>SKU</th>
                    <th>Status</th>
                    <th>ID</th>
                  </tr>
                </thead>
                <tbody>
                  {saveData.map((row) => (
                    <SaveRow key={`${row.lager}_${row.sku}`} row={row} />
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
        <div style={{ width: '25%' }}>
          <h2>4. Schon gezählt</h2>
          <IcInventurGezaehlt />
        </div>
      </Stack>
    </Stack>
  )
}

const blacklist = []
function SaveRow(props) {
  const [rowState, setRowState] = useState({ status: 'wait', ...props.row })
  const key = `${props.row.lager}_${props.row.sku}`
  if (props.row.menge < 0 && rowState.status !== 'error') {
    setRowState({ ...rowState, status: 'error' })
  }
  const [result] = useQuery({
    query: EINEBESTAETIGUNG_QUERY,
    variables: { inventurName: INVENTUR_NAME, lager: props.row.lager, sku: `${props.row.sku}`, cursor: null },
    pause: rowState.status !== 'wait',
  })
  const { data } = result
  const eineBestaetigung = data?.nodes?.edges?.[0]?.node

  if (eineBestaetigung && !['done', 'same', 'booked'].includes(rowState.status)) {
    assert(
      data?.nodes?.edges?.length < 2,
      `zu viele Ergebnisse ${data?.nodes?.edges?.length} für ${props.row}`
    )
    assert(eineBestaetigung.artnr === `${props.row.sku}`, `${eineBestaetigung.artnr} === ${rowState.sku}`)
    assert(eineBestaetigung.lager === rowState.lager, `${eineBestaetigung.lager} === ${rowState.lager}`)
    assert(eineBestaetigung.inventur === INVENTUR_NAME, `${eineBestaetigung.inventur} === ${INVENTUR_NAME}`)
    if (
      eineBestaetigung.bestaetigt ||
      eineBestaetigung.buchungsnr_bestaetigung?.length > 0 ||
      eineBestaetigung.buchungsnr_korrektur?.length > 0
    ) {
      if (rowState.status !== 'booked') {
        setRowState({ ...rowState, status: 'booked' })
      }
    } else if (rowState.status === 'wait') {
      setRowState({ ...rowState, status: 'ready' })
    }

    if (
      ['updating', 'ready'].includes(rowState.status) &&
      eineBestaetigung.zaehlbestand === rowState.menge &&
      eineBestaetigung.status === 'gezählt' &&
      eineBestaetigung.gezaehlt &&
      rowState.status !== 'done'
    ) {
      setRowState({ ...rowState, status: 'done' })
    }
  }
  const [mutateResult, mutateEntity] = useMutation(
    gql`
      mutation schreibeIcIntventurBestaetigung(
        $designator: ID!
        $menge: Int!
        $quelle: String
        $info: String
      ) {
        hwIcIntventurBestaetigungUpdate(
          designator: $designator
          input: { zaehlbestand: $menge, quelle: $quelle, info: $info, status: "gezählt", gezaehlt: true }
        ) {
          ...intventurBestaetigungFragment
        }
      }
      ${INVENTURBESTAETIGUNG_FRAGMENT}
    `
  )
  const [, createEntity] = useMutation(
    gql`
      mutation CreateInventur(
        $artnr: String!
        $lager: String!
        $inventur: String!
        $menge: Int!
        $quelle: String!
        $info: String!
      ) {
        hwIcIntventurBestaetigungCreate(
          input: {
            lager: $lager
            inventur: $inventur
            artnr: $artnr
            zaehlbestand: $menge
            quelle: $quelle
            info: $info
            status: "gezählt"
            gezaehlt: true
          }
        ) {
          ...intventurBestaetigungFragment
        }
      }
      ${INVENTURBESTAETIGUNG_FRAGMENT}
    `
  )

  if (!result.fetching && data?.nodes?.edges && data?.nodes?.edges.length === 0) {
    // Scheinbar kein Zählbedarf gefunden
    if (['wait'].includes(rowState.status)) {
      // create new Row
      if (blacklist.includes(rowState.sku)) {
        console.log('dupe', rowState.sku)
      } else if (rowState.status !== 'updating') {
        blacklist.push(rowState.sku)
        assertIsString(props.row.lager)
        assertIsString(props.row.sku)
        setRowState({ ...rowState, status: 'updating' })
        createEntity({
          menge: props.row.menge,
          quelle: 'GUI',
          info: props.row.info || 'findling',
          lager: props.row.lager,
          inventur: INVENTUR_NAME,
          artnr: props.row.sku,
        })
      }
    }
  }

  if (rowState.status === 'ready') {
    if (eineBestaetigung.zaehlbestand !== rowState.menge && rowState.status !== 'done') {
      if (blacklist.includes(eineBestaetigung.designator)) {
        console.log('dupe', props.row.designator)
      } else if (rowState.status !== 'updating') {
        blacklist.push(props.row.designator)
        setRowState({ ...rowState, status: 'updating' })
        mutateEntity({
          designator: eineBestaetigung.designator,
          menge: props.row.menge,
          quelle: 'GUI',
          info: props.row.info || '',
        })
      }
    } else if (rowState.status !== 'same') {
      console.log('noupdate', rowState.status, eineBestaetigung)
      blacklist.push(eineBestaetigung.designator)
      setRowState({ ...rowState, status: 'same' })
    }
  }
  const { data: mutateData, fetching, error } = mutateResult
  if (error && rowState.status === 'updating') {
    console.error('error', error)
    setRowState({ ...rowState, status: 'error' })
  } else if (mutateData && !fetching && rowState.status === 'saved') {
    if (mutateData.errors) {
      setRowState({
        ...rowState,
        status: 'error',
        designator: mutateData?.errors?.[0]?.message,
      })
    } else {
      setRowState({
        ...rowState,
        status: 'saved',
        designator: mutateData?.hwIcIntventurBestaetigungCreate?.designator,
      })
    }
  }

  // if (['same'].includes(rowState.status)) {
  //   return null
  // }
  return (
    <tr key={key}>
      <td>{props.row.lager}</td>
      <td align="right">{props.row.menge}</td>
      <td>{props.row.sku}</td>
      <td>{rowState.status}</td>
      <td>
        <LinkRender value={eineBestaetigung?.designator} />
      </td>
    </tr>
  )
}
const IcInventurHochlagenUnbestaetigt = () => {
  const [result] = useQuery({
    query: UNGEZAEHLT_QUERY,
    variables: { inventurName: INVENTUR_NAME },
  })
  const { data, fetching, error } = result
  let entities = []
  if (data?.nodes?.edges) {
    entities = data?.nodes?.edges.map((x) => x?.node)
  }
  return (
    <LadeChecker loading={fetching} data={data?.nodes} error={error}>
      <small>{entities.length} Einträge</small>
      <KindLightList
        entities={entities}
        schemaName={'HwIcIntventurBestaetigung'}
        hideExcept={['lager', 'artnr']}
      />
    </LadeChecker>
  )
}
const IcInventurGezaehlt = () => {
  const [result] = useQuery({
    query: GEZAEHLT_QUERY,
    variables: { inventurName: INVENTUR_NAME },
  })
  const { data, fetching, error } = result
  let entities = []
  console.log('data', data)
  if (data?.nodes?.edges) {
    entities = data?.nodes?.edges.map((x) => x?.node)
  }
  return (
    <LadeChecker loading={fetching} data={data?.nodes} error={error}>
      <small>{entities.length} Einträge</small>
      <KindLightList
        entities={entities}
        schemaName={'HwIcIntventurBestaetigung'}
        hideExcept={['lager', 'zaehlbestand', 'artnr']}
      />
    </LadeChecker>
  )
}
